<script>
import {formatZonedDateTime} from "@/baseUtils";
import {mapActions, mapState} from "pinia";
import i18n from "@/i18n";
import {shareStore} from "@/store/shareStore";
import {proposalStore} from "@/store/proposalStore";
import {opinionStore} from "@/store/opinionStore";
import Dialog from "primevue/dialog";
import BaseAvatar from "@/components/BaseAvatar/BaseAvatar.vue";

export default {
  name: "ShareSelectEntityModal",
  components: {
    Dialog,
    BaseAvatar
  },
  data() {
    return {
      shareUrl: ''
    }
  },
  computed: {
    ...mapState(shareStore, [
      'selectShareEntityModalState'
    ]),
    ...mapState(proposalStore, [
      'proposalInfo'
    ]),
    ...mapState(opinionStore, [
      'currentOpinion'
    ]),
    modalState: {
      get() {
        return this.selectShareEntityModalState;
      },
      set() {
        this.closeModal();
      },
    },
    opinionLikeText() {
      const opinionLike = this.currentOpinion.opinionLike;
      let text = "";

      if (opinionLike === 'VERY_GOOD') {
        text = i18n.global.t('opinion.veryGood')
      } else if (opinionLike === 'PRETTY_GOOD') {
        text = i18n.global.t('opinion.prettyGood')
      } else if (opinionLike === 'NO_OPINION') {
        text = i18n.global.t('opinion.noOpinion')
      } else if (opinionLike === 'PRETTY_BAD') {
        text = i18n.global.t('opinion.prettyBad')
      } else if (opinionLike === 'VERY_BAD') {
        text = i18n.global.t('opinion.veryBad')
      }

      return text;
    },
    opinionLikeIconClass() {
      const opinionLike = this.currentOpinion.opinionLike;
      let iconClass = "";

      if (opinionLike === 'VERY_GOOD') {
        iconClass = "very-good";
      } else if (opinionLike === 'PRETTY_GOOD') {
        iconClass = "pretty-good";
      } else if (opinionLike === 'NO_OPINION') {
        iconClass = "no-opinion";
      } else if (opinionLike === 'PRETTY_BAD') {
        iconClass = "pretty-bad";
      } else if (opinionLike === 'VERY_BAD') {
        iconClass = "very-bad";
      }

      return iconClass;
    },
    opinionCreateDate() {
      return formatZonedDateTime(this.currentOpinion.createdDateTime);
    },
    opinionPrivacyIcon() {
      return 'public';
    },
    opinionPrivacyCaption() {
      return 'Public';
    }
  },
  methods: {
    ...mapActions(shareStore,[
      "setShareEntityType",
      "toggleShareModal",
      "toggleSelectShareEntityModal",
    ]),
    selectSharedEntity(type) {
      this.setShareEntityType(type);
      this.toggleSelectShareEntityModal();
      this.toggleShareModal();
    },
    closeModal() {
      this.toggleSelectShareEntityModal();
    },
  }
}
</script>

<template>
  <Dialog v-model:visible="modalState"
          class="share-select-entity-modal"
          modal
          :header="$t('share.whatShare')"
  >
    <div>
      <span class="share-select-entity-title">{{$t('share.selectWhatShare')}}</span>
      <div class="share-select-entity-list">
        <div class="share-select-entity">
          <div class="share-select-entity__header">{{$t('share.proposal')}}</div>
          <div class="share-select-entity__content">
            <div class="forum-flag">{{ proposalInfo.forum.name }}</div>
            <span class="proposal-name">{{ proposalInfo.title }}</span>
            <button type="button" class="select-share-entity-btn" @click="selectSharedEntity('PROPOSAL')">
              {{$t('share.shareProposal')}}
            </button>
          </div>
        </div>
        <div class="share-select-entity-separator"></div>
        <div class="share-select-entity">
          <div class="share-select-entity__header">{{$t('share.opinion')}}</div>
          <div class="share-select-entity__content">
            <div class="d-flex">
              <base-avatar :size="'XL'"
                           class="opinion-owner-avatar"
                           :img-hash="currentOpinion.creator.imgHash"
              />
              <div class="opinion-info">
                <div class="opinion-info__general-info-row">
                  <span class="opinion-info-label">Opinion from:</span>
                  <router-link :to="{name: 'UserView', params: {id: currentOpinion.creator.id}}"
                               target="_blank"
                               class="opinion-owner-name"
                  >{{currentOpinion.creator.name}}</router-link>
                  <div class="opinion-like">
                    <div class="opinion-like__icon" :class="opinionLikeIconClass"></div>
                    <span class="opinion-like__text">{{opinionLikeText}}</span>
                  </div>
                </div>
              </div>
            </div>
            <button type="button" class="select-share-entity-btn" @click="selectSharedEntity('OPINION')">
              {{$t('share.shareOpinion')}}
            </button>
          </div>
        </div>

      </div>
    </div>
    <template #footer>
      <button type="button" class="p-dialog-close-btn" @click="closeModal()">
        {{$t('common.buttons.cancel')}}
      </button>
    </template>
  </Dialog>
</template>

<style lang="scss">
.share-select-entity-modal {
  min-width: 700px;

  .p-dialog-content {
    padding: 20px;
    line-height: 16px;
  }

  .share-select-entity-title {
    display: inline-block;
    margin-bottom: 15px;
    color: #4f4f4f;
  }

  .share-select-entity-list {
    display: flex;

    .share-select-entity-separator {
      width: 1px;
      height: 220px;
      margin: 0 2px;
      background: #f2f2f2;
    }
  }

  .share-select-entity {
    display: flex;
    flex-direction: column;
    width: 330px;
    height: 220px;
    position: relative;
    border-radius: 4px;
    border: solid 1px #d1d1d1;
    background: #fff;

    &:hover {
      background: #f1e8f8;
      .forum-flag {
        &:after {
          border-right: 12px solid #f1e8f8;
        }
      }

      .select-share-entity-btn {
        display: block;
      }
    }

    &__header {
      display: flex;
      align-items: center;
      height: 40px;
      padding: 0 10px;
      border-bottom: solid 1px #d1d1d1;
      font-size: 14px;
      text-transform: uppercase;
      color: #4f4f4f;
    }

    &__content {
      padding: 15px;
      display: flex;
      height: 100%;
      flex-direction: column;
      justify-content: space-between;
    }

    .forum-flag {
      display: flex;
      align-items: center;
      position: absolute;
      height: 24px;
      left: 0;
      width: fit-content;
      padding: 5px 24px 5px 6px;
      margin-bottom: 10px;
      font-size: 14px;
      color: #ffffff;
      background: #7835a6;
      cursor: pointer;

      &:after {
        position: absolute;
        top: 0;
        right: -1px;
        content: '';
        width: 0;
        height: 0;
        border-top: 12px solid transparent;
        border-bottom: 12px solid transparent;
        border-right: 12px solid #fff;
      }

      &__icon {
        width: 16px;
        height: 20px;
        margin-right: 9px;

        &.riksdagen-icon {
          width: 22px;
          height: 15px;
          background: url("~@/assets/img/proposal/riksdagen-icon.svg"), no-repeat, center;
        }
      }
    }

    .proposal-name {
      display: inline-block;
      padding-top: 40px;
      color: #4f4f4f;
      font-size: 14px;
      font-weight: 600;
    }

    .opinion-owner-avatar {
      margin-right: 10px;
    }

    .opinion-info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      &__general-info-row {
        display: flex;
        flex-direction: column;
        margin-bottom: 5px;
        font-size: 14px;
        font-weight: 600;
      }

      &__addition-info-row {
        display: flex;
        align-items: center;
        font-size: 12px;
      }

      .opinion-info-label {
        font-size: 12px;
        color: #3c3c3c;
      }

      .opinion-owner-name {
        margin-bottom: 5px;
      }

      .opinion-like {
        display: flex;
        align-items: center;
        margin-right: 5px;
        &__icon {
          width: 27px;
          height: 27px;
          margin-right: 5px;

          &.very-good {
            background: url("~@/assets/img/opinion/very_good.svg") no-repeat;
          }
          &.pretty-good {
            background: url("~@/assets/img/opinion/pretty_good.svg") no-repeat;
          }
          &.no-opinion {
            background: url("~@/assets/img/opinion/no_opinion.svg") no-repeat;
          }
          &.pretty-bad {
            background: url("~@/assets/img/opinion/pretty_bad.svg") no-repeat;
          }
          &.very-bad {
            background: url("~@/assets/img/opinion/very_bad.svg") no-repeat;
          }
        }
        &__text {
          color: #3c3c3c;
        }
      }
    }

    .select-share-entity-btn {
      display: none;
      width: 100%;
      height: 32px;
      padding: 0 15px;
      background: #7130a0;
      font-family: "Open Sans", Arial, sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: #fff;
      border-radius: 4px;
      border: none;
      outline: none;
      cursor: pointer;
    }
  }
}

</style>